import { useController } from 'react-hook-form'
import { useMemo, useState } from 'react'

/**
 * Rebuild a multi-select's committed array so stored values that have no loaded
 * option survive the edit.
 *
 * `getSelectedOption()` can only hand react-select the values it has options
 * for, and react-select's onChange reports exactly the chips it rendered — so
 * committing that set verbatim would delete anything the widget could not
 * display. That happens in production: markets.list only returns active
 * markets, so a place/user joined to a deactivated market has a stored id with
 * no option, and places.edit rewrites place_market_join from the payload alone.
 * Semantic's Form.Select (replaced by this component) kept unknown values in
 * the value array for the same reason.
 *
 * Unrendered values keep their original position; values still selected keep
 * theirs; newly selected values are appended.
 *
 * @param {*} storedValue - Current RHF field value
 * @param {Array} selected - Values react-select just reported as selected
 * @param {Array} options - Currently loaded options
 * @returns {Array} Value to commit
 */
function mergeUnrenderedValues(storedValue, selected, options) {
  const stored = storedValue || []
  const wasRendered = (value) => options.some((opt) => opt.value === value)

  const merged = stored.filter(
    (value) => !wasRendered(value) || selected.includes(value),
  )
  selected.forEach((value) => {
    if (!merged.includes(value)) {
      merged.push(value)
    }
  })

  return merged
}

/**
 * FormSelect Logic Hook
 * Handles React Hook Form integration for select/dropdown fields using react-select
 *
 * @param {Object} props
 * @param {string} props.name - Field name for RHF
 * @param {Object} props.control - RHF control object
 * @param {*} [props.defaultValue=''] - Default value
 * @param {Object} [props.rules] - RHF validation rules
 * @param {Function} [props.onSearchChange] - Custom search handler
 * @param {Function} [props.onChange] - Custom onChange handler (called after field updates)
 * @param {Array} [props.options] - Options array
 * @returns {Object} Field state and handlers
 */
export function useFormSelect({
  name,
  control,
  defaultValue = '',
  rules,
  onSearchChange,
  onChange: customOnChange,
  options = [],
  multiple = false,
}) {
  const {
    field,
    fieldState: { error, invalid },
  } = useController({
    name,
    control,
    defaultValue,
    rules,
  })

  const [inputValue, setInputValue] = useState('')

  const handleChange = (option, actionMeta) => {
    // react-select hands isMulti an array of selected options (or null)
    let value
    if (multiple) {
      const selected = (option || []).map((opt) => opt.value)
      // Clearing the whole field is an explicit "remove everything" — the only
      // action that may drop values the widget never rendered.
      value =
        actionMeta?.action === 'clear'
          ? selected
          : mergeUnrenderedValues(field.value, selected, options)
    } else {
      value = option?.value ?? null
    }
    field.onChange(value)

    if (customOnChange) {
      customOnChange(value)
    }
  }

  // react-select treats `inputValue` as the menu's search term (its default
  // `filterOption` is `createFilter()`), so the input must only ever hold text
  // the user actually typed. Seeding it with the selected option's label on
  // menu open — Semantic's Dropdown behaviour — filtered every searchable
  // select down to the single value it already held, and made a typed query
  // arrive as `<old label><typed text>`.
  const handleInputChange = (newValue, { action }) => {
    if (action === 'input-change') {
      setInputValue(newValue)
      if (onSearchChange) {
        onSearchChange(newValue)
      }
    } else {
      setInputValue('')
    }
  }

  const getSelectedOption = () => {
    if (multiple) {
      return (field.value || [])
        .map((value) => options.find((opt) => opt.value === value))
        .filter(Boolean)
    }

    if (
      field.value === undefined ||
      field.value === null ||
      field.value === ''
    ) {
      return null
    }

    const found = options.find((opt) => opt.value === field.value) || null
    return found
  }

  // `description` and `className` ride along so the view can render the same
  // secondary line and muted styling Semantic's Dropdown gave an option — they
  // are part of the option shape callers already declare in FormSelect.propTypes.
  const reactSelectOptions = useMemo(
    () =>
      options.map((opt) => ({
        value: opt.value,
        label: opt.text,
        icon: opt.icon,
        description: opt.description,
        className: opt.className,
      })),
    [options],
  )

  const reactSelectValue = useMemo(() => {
    const selectedOption = getSelectedOption()
    if (multiple) {
      return selectedOption.map((opt) => ({
        value: opt.value,
        label: opt.text,
        icon: opt.icon,
        description: opt.description,
        className: opt.className,
      }))
    }
    return selectedOption
      ? {
          value: selectedOption.value,
          label: selectedOption.text,
          icon: selectedOption.icon,
          description: selectedOption.description,
          className: selectedOption.className,
        }
      : null
  }, [field.value, options, multiple])

  return {
    // Field props from RHF
    value: field.value,
    selectedOption: getSelectedOption(),
    onChange: handleChange,
    onBlur: field.onBlur,
    name: field.name,
    ref: field.ref,

    onInputChange: handleInputChange,
    inputValue,

    reactSelectOptions,
    reactSelectValue,

    // Validation state
    error: invalid,
    errorMessage:
      error?.message || (invalid ? 'This field is required' : undefined),
  }
}
